import {Fragment, useEffect, useId, useRef, useState} from "react";
import PropTypes from "prop-types";
import {Transition} from "@headlessui/react";
import {createPortal} from "react-dom";
import classNames from "classnames";
import ModalContentTransition from "./modal-content-transition";

ModalDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    title: PropTypes.node,
    titleClassName: PropTypes.string,
    getInitialFocusElement: PropTypes.func,
    autoFocusOnMount: PropTypes.bool,
    children: PropTypes.node,
    canClose: PropTypes.bool,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    frameClassName: PropTypes.string,
    panelClassName: PropTypes.string,
    bodyClassName: PropTypes.string,
    closeButtonClassName: PropTypes.string,
    closeIconClassName: PropTypes.string,
    closeButtonLabel: PropTypes.string,
    animateContent: PropTypes.bool,
    motion: PropTypes.oneOf(["scale", "fade"]),
    restoreFocus: PropTypes.bool,
    onAfterLeave: PropTypes.func,
    style: PropTypes.object,
    contentKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ModalDialog.defaultProps = {
    isOpen: false,
    onClose: () => {
    },
    titleClassName: "",
    getInitialFocusElement: null,
    autoFocusOnMount: true,
    canClose: true,
    className: "",
    containerClassName: "",
    frameClassName: "",
    panelClassName: "",
    bodyClassName: "",
    closeButtonClassName: "",
    closeIconClassName: "",
    closeButtonLabel: "Close dialog",
    animateContent: false,
    motion: "scale",
    restoreFocus: true,
    onAfterLeave: null,
    style: undefined,
    contentKey: "content",
};

export default function ModalDialog({
                                        isOpen,
                                        onClose,
                                        title,
                                        titleClassName,
                                        getInitialFocusElement,
                                        autoFocusOnMount,
                                        children,
                                        canClose,
                                        className,
                                        containerClassName,
                                        frameClassName,
                                        panelClassName,
                                        bodyClassName,
                                        closeButtonClassName,
                                        closeIconClassName,
                                        closeButtonLabel,
                                        animateContent,
                                        motion,
                                        restoreFocus,
                                        onAfterLeave,
                                        style,
                                        contentKey,
                                    }) {
    const closeButtonRef = useRef(null);
    const fallbackFocusRef = useRef(null);
    const panelRef = useRef(null);
    const dialogRef = useRef(null);
    const activeElementRef = useRef(null);
    const [isMounted, setIsMounted] = useState(false);
    const titleId = useId();

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const handleClose = event => {
        event?.stopPropagation?.();
        if (!canClose) return;
        onClose?.();
    };

    useEffect(() => {
        if (!isOpen || typeof document === "undefined") return;

        activeElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

        const handleKeyDown = (event) => {
            if (event.key === "Tab") {
                const focusableElements = dialogRef.current
                    ? Array.from(dialogRef.current.querySelectorAll(
                        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
                    )).filter((element) => !element.hasAttribute("aria-hidden"))
                    : [];

                if (focusableElements.length === 0) {
                    event.preventDefault();
                    fallbackFocusRef.current?.focus?.();
                    return;
                }

                const firstFocusableElement = focusableElements[0];
                const lastFocusableElement = focusableElements[focusableElements.length - 1];
                const activeElement = document.activeElement;

                if (event.shiftKey && (activeElement === firstFocusableElement || !dialogRef.current?.contains(activeElement))) {
                    event.preventDefault();
                    lastFocusableElement.focus();
                    return;
                }

                if (!event.shiftKey && (activeElement === lastFocusableElement || !dialogRef.current?.contains(activeElement))) {
                    event.preventDefault();
                    firstFocusableElement.focus();
                }
                return;
            }

            if (event.key !== "Escape") return;
            if (!canClose) {
                event.preventDefault();
                return;
            }

            event.preventDefault();
            handleClose();
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, canClose, getInitialFocusElement]);

    useEffect(() => {
        if (isOpen || !activeElementRef.current) return;
        if (restoreFocus) activeElementRef.current.focus?.({preventScroll: true});
        activeElementRef.current = null;
    }, [isOpen, restoreFocus]);

    if (!isMounted || typeof document === "undefined") return null;

    const modalContent = (
        <>
            {title && (
                <h3
                    id={titleId}
                    data-title=""
                    className={classNames("text-center text-gray-900 !mb-4 !text-2xl font-semibold leading-tight", titleClassName)}
                >
                    {title}
                </h3>
            )}
            <div className={bodyClassName}>
                {children}
            </div>
        </>
    );

    return createPortal(
        <Transition appear show={isOpen} as={Fragment} afterLeave={onAfterLeave || undefined}>
            <div className={classNames("relative z-50", className)} style={style}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" onClick={handleClose}/>
                </Transition.Child>

                <div className={classNames("fixed inset-0 overflow-y-auto overscroll-contain", containerClassName)} onClick={handleClose}>
                    <div className={classNames("flex min-h-full items-center justify-center p-4 text-left sm:p-6", frameClassName)}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom={motion === "fade" ? "opacity-0" : "opacity-0 scale-95"}
                            enterTo={motion === "fade" ? "opacity-100" : "opacity-100 scale-100"}
                            leave="ease-in duration-200"
                            leaveFrom={motion === "fade" ? "opacity-100" : "opacity-100 scale-100"}
                            leaveTo={motion === "fade" ? "opacity-0" : "opacity-0 scale-95"}
                        >
                            <div
                                ref={dialogRef}
                                className="relative w-full"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby={title ? titleId : undefined}
                            >
                                <div
                                    ref={(element) => {
                                        panelRef.current = element;
                                        fallbackFocusRef.current = element;

                                        if (!element || !isOpen || !autoFocusOnMount || typeof document === "undefined") return;
                                        if (element.contains(document.activeElement)) return;

                                        const focusTarget = getInitialFocusElement?.(element) ?? element;
                                        if (focusTarget === document.activeElement) return;
                                        focusTarget?.focus?.();
                                    }}
                                    tabIndex={-1}
                                    className={classNames(
                                        "bg-white rounded-theme shadow-lg w-full mx-auto max-h-[75vh] overflow-y-auto overscroll-contain p-6 focus:outline-none sm:w-[38rem]",
                                        panelClassName
                                    )}
                                    data-popup=""
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {animateContent ? (
                                        <ModalContentTransition transitionKey={contentKey}>
                                            {modalContent}
                                        </ModalContentTransition>
                                    ) : (
                                        modalContent
                                    )}
                                </div>
                                <button
                                    ref={closeButtonRef}
                                    type="button"
                                    onClick={handleClose}
                                    disabled={!canClose}
                                    aria-disabled={!canClose}
                                    aria-label={closeButtonLabel}
                                    className={classNames(
                                        "absolute left-1/2 mt-4 -translate-x-1/2 border-2 border-gray-300 bg-white rounded-full shadow-lg overflow-hidden",
                                        canClose ? "hover:border-gray-400" : "cursor-not-allowed opacity-40",
                                        closeButtonClassName
                                    )}
                                >
                                    <svg className={classNames("h-8 w-8 stroke-gray-800 bg-gray-100 p-1", closeIconClassName)} fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                        </Transition.Child>
                    </div>
                </div>
            </div>
        </Transition>
        ,
        document.body
    );
}
